import { createMemo, onMount } from "solid-js";
import ManeuverBoxPlot from "../../charts/ManeuverBoxPlot";
import { MANEUVER_BASELINE_DERIVED_CHANNELS } from "../../../utils/maneuverBaselineLossColumns";
import { tabledata, eventType, phase } from "../../../store/globalStore";
import { setTriggerUpdate } from "../../../store/selectionStore";
import { TAKEOFF_SCATTER_CHANNELS, getManeuverScatterChannelsForEventType } from "../../../utils/maneuversConfig";

/** Chart order for TAKEOFF scatter: big-table columns minus the TWS ones (already the x-axis). */
const charts_order_takeoff: string[] = [...TAKEOFF_SCATTER_CHANNELS];

interface ScatterProps {
  context?: string;
}

export default function Scatter(_props: ScatterProps) {
  onMount(() => {
    requestAnimationFrame(() => {
      if (tabledata().length > 0) {
        setTriggerUpdate(true);
      }
    });
  });

  const channels = createMemo((): string[] => {
    const rows = tabledata();
    if (!rows || rows.length === 0) return [];
    const isTakeoff = (eventType() || '').toUpperCase() === 'TAKEOFF';
    if (isTakeoff) {
      return [...charts_order_takeoff];
    }

    const sample = rows[0];
    const orderToUse = getManeuverScatterChannelsForEventType(eventType() || 'TACK', phase());

    // Create exclude set with lowercase versions for case-insensitive matching
    // Exclude State and Config as they are color-by channels, not visualization channels
    const excludeLower = new Set([
      'datetime','event_id','tws_avg','tws_bin','twa_entry','tack','race_number','leg_number','event','year','source_name','config','state','time','sink_min','grade','tack_side','row_num','twd_delta',
      // Baseline deltas are written onto the rows for the tables; they are not measured channels,
      // and each one left in here would spawn a chart of its own.
      ...MANEUVER_BASELINE_DERIVED_CHANNELS,
    ]);
    
    // Normalize channel names to lowercase and deduplicate using a Set
    // This prevents duplicates from case variations like "Source_name" and "source_name"
    const seenLowercase = new Set<string>();
    const normalizedChannels: string[] = [];
    
    for (const key of Object.keys(sample)) {
      const keyLower = key.toLowerCase();
      // Skip if excluded (case-insensitive) or already seen
      if (excludeLower.has(keyLower) || seenLowercase.has(keyLower)) {
        // Mark as seen even if excluded, to prevent duplicates
        seenLowercase.add(keyLower);
        continue;
      }
      // Add to seen set and include in channels
      seenLowercase.add(keyLower);
      // Use the original case from the first occurrence
      normalizedChannels.push(key);
    }
    
    // When charts_order is set: only draw those channels, in that order. Otherwise draw all.
    if (orderToUse.length > 0) {
      const byLower = new Map(normalizedChannels.map(c => [c.toLowerCase(), c]));
      const ordered: string[] = [];
      for (const name of orderToUse) {
        const key = name.toLowerCase();
        // Use exact key from first row when present; otherwise use lowercase so chart can resolve via case-insensitive lookup
        const channelKey = byLower.get(key) ?? key;
        ordered.push(channelKey);
      }
      return ordered;
    }
    return normalizedChannels;
  });

  return (
    <div class="maneuver-scatter-root maneuver-scatter-grouped-root">
      <ManeuverBoxPlot channels={channels()} />
    </div>
  );
}
